import { Box, Grid, Stack, useMediaQuery } from '@chakra-ui/react';
import type { ReactNode } from 'react';

import { breakpoints } from '#features/ui/theme/breakpoints';
import { AuthCharacterMarquee } from '../AuthCharacterMarquee';
import { AuthLanguageSelector } from '../AuthLanguageSelector';

/** Account entry: a separate desktop scene and a fading compact welcome background. */
export const AuthPageLayout = ({
  children,
  artwork,
  languageDisabled = false,
}: {
  children: ReactNode;
  artwork?: (compact: boolean) => ReactNode;
  languageDisabled?: boolean;
}) => {
  const [wideLayout] = useMediaQuery([`(min-width: ${breakpoints.lg})`], {
    ssr: true,
    fallback: [false],
  });

  return (
    <Grid
      w="100%"
      maxW="6xl"
      mx="auto"
      position="relative"
      py={{ base: 6, lg: 12 }}
      templateColumns={{ base: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' }}
      alignItems="center"
      gap={{ base: 6, lg: 12 }}
      css={{
        '--auth-art-space':
          'clamp(var(--chakra-spacing-20), calc(32svh - var(--chakra-spacing-24)), var(--chakra-spacing-48))',
      }}
    >
      <Box
        display={{ base: 'block', lg: 'none' }}
        position="absolute"
        top={0}
        insetInline={-3}
        maxW="md"
        mx="auto"
        h="calc(var(--auth-art-space) + var(--chakra-spacing-6))"
        pointerEvents="none"
        aria-hidden="true"
        opacity={0.9}
        css={{ maskImage: 'linear-gradient(to bottom, black 25%, transparent 100%)' }}
      >
        {!wideLayout ? artwork ? artwork(true) : <AuthCharacterMarquee compact /> : null}
      </Box>
      <Box display={{ base: 'none', lg: 'block' }} aspectRatio="1" aria-hidden="true">
        {wideLayout ? artwork ? artwork(false) : <AuthCharacterMarquee /> : null}
      </Box>
      <Stack
        gap={6}
        w="md"
        maxW="100%"
        mx="auto"
        minW={0}
        position="relative"
        data-auth-controls=""
        pt={{ base: 'var(--auth-art-space)', lg: 0 }}
      >
        {children}
        <AuthLanguageSelector disabled={languageDisabled} />
      </Stack>
    </Grid>
  );
};
